<template>
  <div id="contenedor-tablero-expedientes-detalle">
    <v-container fluid>
      <v-card flat class="mb-4 rounded-lg">
        <v-card-text class="px-0 py-1">
          <TituloToolBar
            titulo="Detalle de Expediente"
            regresar>
          </TituloToolBar>
        </v-card-text>
      </v-card>

      <v-skeleton-loader
        v-if="$apollo.loading"
        type="card, article, table-heading, table-tbody">
      </v-skeleton-loader>

      <v-alert v-else-if="errorConsulta" type="error" outlined class="mb-4">
        Ocurrió un error al consultar el expediente del contrato. Intente nuevamente.
      </v-alert>

      <template v-else-if="contrato">
        <!-- Encabezado -->
        <v-card
          flat
          class="mb-5 rounded-xl card-encabezado"
          :class="claseSemaforo(contrato.semaforo)">
          <v-card-text class="pa-6">
            <v-row align="center">
              <v-col cols="12" md="8" class="d-flex align-start">
                <v-avatar size="56" color="white" class="elevation-2 mr-4 flex-shrink-0">
                  <v-icon size="28" :color="obtenerEstado(contrato.estadoExpediente).color">
                    mdi-folder-multiple-outline
                  </v-icon>
                </v-avatar>
                <div>
                  <div class="text-h5 font-weight-bold">{{ contrato.numero }}</div>
                  <div class="text-body-2 grey--text text--darken-1 mt-1">{{ contrato.meta }}</div>
                  <div class="d-flex flex-wrap align-center mt-3">
                    <v-chip
                      v-if="contrato.estatus"
                      small
                      class="mr-2 mb-1"
                      :color="contrato.estatus.color"
                      :dark="contrato.estatus.esTextoClaro">
                      {{ contrato.estatus.nombre }}
                    </v-chip>
                    <v-chip
                      small
                      class="mr-2 mb-1"
                      :color="obtenerEstado(contrato.estadoExpediente).color"
                      :dark="obtenerEstado(contrato.estadoExpediente).dark">
                      <v-icon left small>mdi-circle-medium</v-icon>
                      {{ obtenerEstado(contrato.estadoExpediente).nombre }}
                    </v-chip>
                  </div>
                  <v-row class="mt-4" no-gutters>
                    <v-col cols="6" sm="4">
                      <div class="text-overline meta-label">Fecha</div>
                      <div class="d-flex align-center">
                        <v-icon small class="mr-1" color="grey darken-1">mdi-calendar</v-icon>
                        {{ $moment(contrato.fecha).format('DD/MM/YYYY') }}
                      </div>
                    </v-col>
                    <v-col cols="6" sm="4">
                      <div class="text-overline meta-label">Contratista</div>
                      <div class="d-flex align-center text-truncate">
                        <v-icon small class="mr-1" color="grey darken-1">mdi-domain</v-icon>
                        <span class="text-truncate">
                          {{ contrato.contratista ? contrato.contratista.nombreRazonSocial : '-' }}
                        </span>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="4">
                      <div class="text-overline meta-label">Responsable</div>
                      <div class="d-flex align-center text-truncate">
                        <v-icon small class="mr-1" color="grey darken-1">mdi-account-tie</v-icon>
                        <span class="text-truncate">{{ responsablePrincipal }}</span>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-center">
                <v-progress-circular
                  :value="contrato.porcentajeIntegracion || 0"
                  :color="obtenerEstado(contrato.estadoExpediente).color"
                  size="120"
                  width="12">
                  <span class="text-h5 font-weight-bold">
                    {{ contrato.porcentajeIntegracion !== null
                      ? `${$numberRound(contrato.porcentajeIntegracion)}%` : '-' }}
                  </span>
                </v-progress-circular>
                <div class="text-caption grey--text mt-3">
                  {{ contrato.integrados }} de {{ contrato.documentosAplicables }}
                  documentos aplicables integrados
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Progreso por etapa -->
        <v-row class="mb-1">
          <v-col
            v-for="etapaResumen in contrato.totalesPorEtapa"
            :key="etapaResumen.etapa"
            cols="12"
            md="4">
            <v-card :class="['card-kpi', claseEtapa(etapaResumen.etapa)]" elevation="2" hover>
              <v-card-text class="pa-5">
                <div class="d-flex align-start mb-3">
                  <v-avatar :color="colorEtapa(etapaResumen.etapa)" size="44" class="mr-3">
                    <v-icon color="white">{{ iconoEtapa(etapaResumen.etapa) }}</v-icon>
                  </v-avatar>
                  <div class="flex-grow-1">
                    <span class="text-caption text--secondary text-uppercase etapa-titulo">
                      {{ nombreEtapa(etapaResumen.etapa) }}
                    </span>
                    <div class="text-h6 font-weight-bold">
                      {{ etapaResumen.porcentajeIntegracion !== null
                        ? `${$numberRound(etapaResumen.porcentajeIntegracion)}%` : '-' }}
                    </div>
                  </div>
                </div>
                <v-progress-linear
                  :value="etapaResumen.porcentajeIntegracion || 0"
                  height="6"
                  rounded
                  :color="colorEtapa(etapaResumen.etapa)">
                </v-progress-linear>
                <div class="text-caption grey--text mt-2">
                  {{ etapaResumen.integrados }} integrados
                  · {{ etapaResumen.faltantes }} faltantes
                  · {{ etapaResumen.noAplicables }} no aplican
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Documentos -->
        <v-row class="mb-3 mt-2">
          <v-col cols="12" md="5">
            <custom-text-field
              label="Buscar documento..."
              append-icon="mdi-magnify"
              clearable
              @keyup="buscar"
              @click:clear="limpiarBuscador">
            </custom-text-field>
          </v-col>
        </v-row>

        <v-card
          v-for="etapaId in etapasVisibles"
          :key="etapaId"
          flat
          class="mb-5 rounded-lg overflow-hidden card-etapa-documentos">
          <div class="etapa-header pa-4 d-flex align-center" :class="`etapa-header-${etapaId}`">
            <v-icon color="white" class="mr-2">{{ iconoEtapa(etapaId) }}</v-icon>
            <span class="white--text text-subtitle-1 font-weight-medium">
              {{ nombreEtapa(etapaId) }}
            </span>
            <v-chip small class="ml-3" color="white">
              {{ documentosPorEtapa(etapaId).length }}
            </v-chip>
          </div>

          <v-card-text class="pa-0">
            <v-alert
              v-if="documentosPorEtapa(etapaId).length === 0"
              type="info"
              text
              dense
              class="ma-4">
              No hay documentos en esta etapa.
            </v-alert>
            <div v-else>
              <div
                v-for="documento in documentosPorEtapa(etapaId)"
                :key="documento.id"
                class="documento-fila d-flex align-center px-4 py-3"
                :class="claseEstadoDocumento(documento.estado)">
                <div class="orden-badge mr-4 flex-shrink-0">{{ documento.orden || '-' }}</div>

                <div class="flex-grow-1 mr-3" style="min-width: 0">
                  <div class="d-flex align-center flex-wrap">
                    <span class="font-weight-medium mr-2">{{ documento.descripcion }}</span>
                    <v-chip
                      v-if="documento.referencia"
                      x-small
                      outlined
                      color="grey darken-1"
                      class="mr-2">
                      {{ documento.referencia }}
                    </v-chip>
                    <v-icon v-if="!documento.aplica" x-small color="grey" class="mr-1">
                      mdi-cancel
                    </v-icon>
                  </div>
                  <div v-if="documento.observaciones" class="text-caption grey--text text-truncate">
                    {{ documento.observaciones }}
                  </div>
                </div>

                <div
                  class="text-caption grey--text text-right mr-4 flex-shrink-0 d-none d-sm-block">
                  {{ $moment(documento.updatedAt).format('DD/MM/YYYY HH:mm') }}
                </div>

                <v-chip
                  small
                  class="mr-3 flex-shrink-0"
                  :color="colorEstadoDocumento(documento.estado)"
                  text-color="white">
                  {{ labelEstadoDocumento(documento.estado) }}
                </v-chip>

                <div class="flex-shrink-0" style="width: 36px">
                  <v-tooltip v-if="documento.publicKey" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="verDocumento(documento)">
                        <v-icon small color="primary">mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>Ver</span>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-dialog
          v-if="documentoAVer"
          v-model="mostrarVerDocumento"
          width="1200"
          scrollable>
          <v-card class="rounded-lg">
            <v-card-title class="grey darken-3 white--text text-subtitle-1 py-3">
              <v-icon left small color="white">mdi-file-eye</v-icon>
              Vista Previa del Documento
              <v-spacer></v-spacer>
              <v-btn icon dark small @click="cerrarVerDocumento">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pa-4">
              <ver-documento-pdf
                :documentoPdfVer="documentoAVer"
                :nombreDescarga="nombreDescargaDocumento(documentoAVer)"
                moduloOrigen="planeacion"
                v-on:cerrar="cerrarVerDocumento">
              </ver-documento-pdf>
            </v-card-text>
          </v-card>
        </v-dialog>
      </template>
    </v-container>
  </div>
</template>

<script>
import TituloToolBar from '@/components/TituloToolBar.vue';
import CustomTextField from '@/components/inputs/sinColumnas/TextField.vue';
import verDocumentoPdf from '@/components/VistaDocumentoPdf.vue';
import { obtenerEstado } from '@/helpers/expedienteEstados';
import TABLERO_EXPEDIENTE_DETALLE from '@/graphql/querys/administracion/tableroExpedientes/tableroExpedienteDetalle.gql';

const NOMBRES_ETAPA = {
  0: 'Sin Etapa',
  1: 'Antes de Ejecución',
  2: 'Durante Ejecución',
  3: 'Después de Ejecución',
};

const ICONOS_ETAPA = {
  0: 'mdi-help-box-outline',
  1: 'mdi-calendar-start-outline',
  2: 'mdi-progress-wrench',
  3: 'mdi-calendar-check-outline',
};

const COLORES_ETAPA = {
  0: '#616161',
  1: '#1976d2',
  2: '#f57c00',
  3: '#00897b',
};

export default {
  name: 'TableroExpedientesDetalleDefault',
  components: {
    TituloToolBar,
    CustomTextField,
    verDocumentoPdf,
  },
  data() {
    return {
      errorConsulta: null,
      buscarTexto: '',
      debounce: null,
      mostrarVerDocumento: false,
      documentoAVer: null,
      tableroExpedienteDetalle: null,
    };
  },
  apollo: {
    tableroExpedienteDetalle: {
      query: TABLERO_EXPEDIENTE_DETALLE,
      variables() {
        return { idContrato: this.$route.params.idContrato };
      },
      result(result) {
        if (!result.loading) {
          this.errorConsulta = result.error || null;
        }
      },
      error(error) {
        this.errorConsulta = error;
      },
    },
  },
  methods: {
    nombreEtapa(etapa) {
      return NOMBRES_ETAPA[etapa] || 'Etapa';
    },
    iconoEtapa(etapa) {
      return ICONOS_ETAPA[etapa] || 'mdi-calendar';
    },
    colorEtapa(etapa) {
      return COLORES_ETAPA[etapa] || '#616161';
    },
    claseEtapa(etapa) {
      return `card-etapa-${etapa}`;
    },
    buscar(e) {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.buscarTexto = e.target.value;
      }, 400);
    },
    limpiarBuscador() {
      this.buscarTexto = '';
    },
    documentosPorEtapa(etapa) {
      if (!this.contrato) return [];
      const texto = (this.buscarTexto || '').trim().toLowerCase();
      return this.contrato.documentos
        .filter((documento) => documento.etapa === etapa)
        .filter((documento) => {
          if (!texto) return true;
          return documento.descripcion.toLowerCase().includes(texto)
            || (documento.referencia && documento.referencia.toLowerCase().includes(texto));
        })
        .slice()
        .sort((a, b) => (a.orden || 0) - (b.orden || 0));
    },
    obtenerEstado,
    claseSemaforo(semaforo) {
      return `card-encabezado-${(semaforo || 'gris').toLowerCase()}`;
    },
    colorEstadoDocumento(estado) {
      const colores = {
        INTEGRADO: 'success',
        PENDIENTE: 'warning',
        NO_APLICA: 'grey',
      };
      return colores[estado] || 'grey';
    },
    labelEstadoDocumento(estado) {
      const etiquetas = {
        INTEGRADO: 'Integrado',
        PENDIENTE: 'Pendiente',
        NO_APLICA: 'No Aplica',
      };
      return etiquetas[estado] || estado;
    },
    claseEstadoDocumento(estado) {
      return `fila-${(estado || '').toLowerCase().replace('_', '-')}`;
    },
    nombreDescargaDocumento(documento) {
      const partes = [this.contrato.numero, documento.descripcion, documento.referencia]
        .filter(Boolean);
      return `${partes.join('_')}.pdf`.replace(/[\\/:*?"<>|]/g, '_');
    },
    verDocumento(documento) {
      this.documentoAVer = documento;
      this.mostrarVerDocumento = true;
    },
    cerrarVerDocumento() {
      this.mostrarVerDocumento = false;
      this.documentoAVer = null;
    },
  },
  computed: {
    contrato() {
      return this.tableroExpedienteDetalle;
    },
    responsablePrincipal() {
      if (!this.contrato) return '-';
      const responsable = this.contrato.responsables.find(({ principal }) => principal);
      return responsable ? responsable.funcionario.nombre : '-';
    },
    etapasVisibles() {
      if (!this.contrato) return [1, 2, 3];
      return this.contrato.totalesPorEtapa.map(({ etapa }) => etapa);
    },
  },
};
</script>

<style lang="scss" scoped>
.meta-label {
  font-size: 0.68rem !important;
  letter-spacing: 0.5px;
  color: #90a4ae;
}

// Encabezado
.card-encabezado {
  border-left: 6px solid #9e9e9e;
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);

  &.card-encabezado-verde {
    border-left-color: #388e3c;
    background: linear-gradient(135deg, #ffffff 0%, #f1f8f4 100%);
  }
  &.card-encabezado-azul {
    border-left-color: #1976d2;
    background: linear-gradient(135deg, #ffffff 0%, #f0f6fd 100%);
  }
  &.card-encabezado-amarillo {
    border-left-color: #f9a825;
    background: linear-gradient(135deg, #ffffff 0%, #fffbf0 100%);
  }
  &.card-encabezado-rojo {
    border-left-color: #d32f2f;
    background: linear-gradient(135deg, #ffffff 0%, #fef5f5 100%);
  }
  &.card-encabezado-gris {
    border-left-color: #9e9e9e;
  }
}

// Tarjetas de etapa (mismo lenguaje visual que el resto del módulo)
.card-kpi {
  border-radius: 12px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 4px solid transparent;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
  }

  &.card-etapa-1 {
    border-left-color: #1976d2;
  }
  &.card-etapa-2 {
    border-left-color: #f57c00;
  }
  &.card-etapa-3 {
    border-left-color: #00897b;
  }
  &.card-etapa-0 {
    border-left-color: #616161;
  }

  .etapa-titulo {
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.5px;
  }
}

// Secciones de documentos por etapa
.card-etapa-documentos {
  border: 1px solid #eceff1;
}

.etapa-header {
  &.etapa-header-1 { background: #1976d2; }
  &.etapa-header-2 { background: #f57c00; }
  &.etapa-header-3 { background: #00897b; }
  &.etapa-header-0 { background: #616161; }
}

.documento-fila {
  border-left: 3px solid transparent;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #fafafa;
  }

  &.fila-integrado {
    border-left-color: #4caf50;
  }
  &.fila-pendiente {
    border-left-color: #ff9800;
  }
  &.fila-no-aplica {
    border-left-color: #bdbdbd;
    opacity: 0.75;
  }
}

.orden-badge {
  min-width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #eceff1;
  color: #546e7a;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
